import Cookies from 'js-cookie'

// Cookie configuration options
interface CookieOptions {
  expires?: number | Date
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'strict' | 'lax' | 'none'
  httpOnly?: boolean
}

// Domain cookie utilities interface
export interface DomainCookieUtils {
  setAccessToken: (token: string) => void
  getAccessToken: () => string | undefined
  removeAccessToken: () => void
  setRefreshToken: (token: string) => void
  getRefreshToken: () => string | undefined
  removeRefreshToken: () => void
  clearAuthCookies: () => void
  hasAccessToken: () => boolean
  hasRefreshToken: () => boolean
}

// Known domain namespaces for clearAllDomainCookies
const KNOWN_DOMAINS = ['admin', 'fingerprinting', 'immigration'] as const
export type DomainNamespace = (typeof KNOWN_DOMAINS)[number]

// Default secure cookie options
const defaultCookieOptions: CookieOptions = {
  expires: 7, // 7 days
  path: '/',
  secure: import.meta.env.PROD, // Only use secure cookies in production (HTTPS)
  sameSite: 'strict', // Strict SameSite policy for CSRF protection
  // Note: httpOnly cannot be set from client-side JavaScript
  // This would need to be set by the server when sending cookies
}

// Shorter expiration for access tokens
const accessTokenOptions: CookieOptions = {
  expires: 1, // 1 day
  path: '/',
  secure: import.meta.env.PROD,
  sameSite: 'strict',
}

// Longer expiration for refresh tokens
const refreshTokenOptions: CookieOptions = {
  expires: 30, // 30 days
  path: '/',
  secure: import.meta.env.PROD,
  sameSite: 'strict',
}

/**
 * Factory function to create domain-specific cookie utilities
 * @param namespace - Domain namespace (e.g., 'admin', 'fingerprinting', 'immigration')
 * @returns DomainCookieUtils with namespaced cookie keys
 */
export function createDomainCookieUtils(namespace: string): DomainCookieUtils {
  const accessTokenKey = `${namespace}_access_token`
  const refreshTokenKey = `${namespace}_refresh_token`

  return {
    setAccessToken: (token: string): void => {
      Cookies.set(accessTokenKey, token, accessTokenOptions)
    },

    getAccessToken: (): string | undefined => {
      return Cookies.get(accessTokenKey)
    },

    removeAccessToken: (): void => {
      Cookies.remove(accessTokenKey, accessTokenOptions)
    },

    setRefreshToken: (token: string): void => {
      Cookies.set(refreshTokenKey, token, refreshTokenOptions)
    },

    getRefreshToken: (): string | undefined => {
      return Cookies.get(refreshTokenKey)
    },

    removeRefreshToken: (): void => {
      Cookies.remove(refreshTokenKey, refreshTokenOptions)
    },

    clearAuthCookies: (): void => {
      Cookies.remove(accessTokenKey, accessTokenOptions)
      Cookies.remove(refreshTokenKey, refreshTokenOptions)
    },

    hasAccessToken: (): boolean => {
      return !!Cookies.get(accessTokenKey)
    },

    hasRefreshToken: (): boolean => {
      return !!Cookies.get(refreshTokenKey)
    },
  }
}

/**
 * Clear all known domain cookies (admin, fingerprinting, immigration)
 */
export function clearAllDomainCookies(): void {
  for (const domain of KNOWN_DOMAINS) {
    const domainUtils = createDomainCookieUtils(domain)
    domainUtils.clearAuthCookies()
  }
}

// Export cookie options for potential customization
export { accessTokenOptions, refreshTokenOptions, defaultCookieOptions }
export type { CookieOptions } 